<template>
  <div class="container py-5 pt-3 pt-xl-5">
    <div
      v-if="messageSent === MessageStatusEnum.sent"
      class="alert alert-success mt-3"
      role="alert"
    >
      {{ $t("signup.new_country.success_message") }}
    </div>
    <div
      v-if="messageSent === MessageStatusEnum.failed"
      class="alert alert-danger mt-3"
      role="alert"
    >
      {{ $t("signup.new_country.error_message") }}
    </div>
    <div
      v-if="messageSent === MessageStatusEnum.rateLimited"
      class="alert alert-warning mt-3"
      role="alert"
    >
      {{ $t("signup.message_rate_limited") }}
    </div>
    <CountryPickerComponent />

    <LoadingOverlayComponent v-if="isLoading" />
    <AccountDataDebugComponent :accountData="accountData" class="mb-4" />

    <div class="mb-4">
      <h4>
        <p>{{ $t("signup.new_country.ask_for_message") }}</p>
      </h4>
      <p>{{ $t("signup.new_country.ask_for_message_2") }}</p>
      <textarea
        class="form-control mb-3"
        rows="6"
        v-model="userMessage"
        :maxlength="SIGNUP_MESSAGE_MAX_LENGTH"
        :placeholder="$t('signup.new_country.placeholder')"
      ></textarea>
      <button
        class="btn btn-primary"
        @click="sendMessage"
        :disabled="
          messageSent === MessageStatusEnum.sending ||
          messageSent === MessageStatusEnum.sent ||
          messageSent === MessageStatusEnum.rateLimited
        "
      >
        {{
          messageSent === MessageStatusEnum.unsent ||
          messageSent === MessageStatusEnum.failed ||
          messageSent === MessageStatusEnum.rateLimited
            ? $t("signup.new_country.send_message")
            : $t("signup.new_country.message_sent")
        }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { isAxiosError } from "axios";
import { ref, onMounted } from "vue";
import CountryPickerComponent from "@/signup/CountryPickerComponent.vue";
import AccountDataDebugComponent from "@/components/common/AccountDataDebugComponent.vue";
import LoadingOverlayComponent from "@/components/common/LoadingOverlayComponent.vue";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useSignupStore } from "@/signup/signup";
import { SIGNUP_MESSAGE_MAX_LENGTH } from "@/signup/deliverNoteLimits";
import { storeToRefs } from "pinia";

enum MessageStatusEnum {
  unsent,
  sending,
  sent,
  failed,
  rateLimited,
}

const { account: accountData, country: selectedCountry } =
  storeToRefs(useSignupStore());

const isLoading = ref(true);
const userMessage = ref("");
const messageSent = ref(MessageStatusEnum.unsent);

const sendMessage = async () => {
  try {
    messageSent.value = MessageStatusEnum.sending;
    const data = {
      message: userMessage.value,
      country: selectedCountry.value,
    };
    const response = await goSolidApi.post(
      "/api/utils/signup/deliver-note",
      data
    );
    if (response.status === 200) {
      messageSent.value = MessageStatusEnum.sent;
    } else {
      messageSent.value = MessageStatusEnum.failed;
      throw new Error("Unexpected response from API");
    }
  } catch (error: unknown) {
    // The backend caps messages per account (HTTP 429). Point the user to support instead of
    // inviting a retry that would fail the same way.
    messageSent.value =
      isAxiosError(error) && error.response?.status === 429
        ? MessageStatusEnum.rateLimited
        : MessageStatusEnum.failed;
    console.error("Error fetching data:", error);
  }
};

onMounted(async () => {
  isLoading.value = false;
});
</script>
